import { fetchGraphQL } from 'libs/contentful/contentful';
import { ExperienceConfiguration } from '@ninetailed/experience.js-react';
import { ContentfulModule } from 'libs/contentful/props';
import { ExperienceMapper as GraphQLExperienceMapper } from '@ninetailed/experience.js-utils';
import { getModuleFragments } from 'libs/contentful/fragments';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { ModuleTypes, ModuleTypeName } from 'utils/modules';
import { MAX_MODULES_PER_PAGE_SECTION } from 'libs/contentful/queries/page/constants';
import { pascalToCamel } from 'utils/formatters';
import { appendFragmentsToQuery } from 'libs/contentful/fragmentUtils';
import { Reference } from '@ninetailed/experience.js-shared';
import { filterValidExperiences } from 'utils/experiences';
import { processSectionModuleExperiences, SECTION_MODULE_CONFIG } from 'utils/sectionModules';

// Extend ExperienceConfiguration to include snowflakeName
export type ExtendedExperienceConfiguration<T extends Reference> = ExperienceConfiguration<T> & {
  snowflakeName?: string;
};

const MAX_EXPERIENCES_PER_PAGE_SECTION = 5;
const MAX_VARIANTS_PER_EXPERIENCE = 10;
const CONTENTFUL_QUERY_SIZE_LIMIT = 16000;

const isString = (value: unknown): value is string => typeof value === 'string';

const getUniqueTypenames = (items: Array<{ __typename?: string }>) =>
  Array.from(new Set(items.map(item => item.__typename).filter(isString)));

type NinetailedResponse = {
  sys: { id: string };
  __typename: string;
  modulesCollection: { items: Array<ContentfulModule> };
  ntExperiencesCollection: {
    items: Array<{
      sys: { id: string };
      ntName?: string;
      snowflakeName?: string;
      ntDescription?: string;
      ntAudience?: { sys: { id: string }; ntName?: string };
      ntVariantsCollection: { items: Array<{ sys: { id: string } }> };
    }>;
  };
};

type PageSectionTypenamesResponse = {
  data?: {
    pageSection?: {
      modulesCollection?: {
        items: Array<{ __typename?: string }>;
      };
    };
  };
};

type VariantModuleTypenamesResponse = {
  data?: Record<
    string,
    | {
        modulesCollection?: {
          items?: Array<{ __typename?: string }>;
        };
      }
    | undefined
  >;
};

type IndividualModuleResponse = {
  sys: { id: string };
  __typename: string;
  internalName?: string;
  ntExperiencesCollection: {
    items: Array<{
      sys: { id: string };
      ntName?: string;
      ntType?: string;
      snowflakeName?: string;
      ntDescription?: string;
      ntAudience?: { sys: { id: string }; ntName?: string };
      ntVariantsCollection: { items: Array<{ sys: { id: string } }> };
    }>;
  };
} & Record<string, unknown>;

export type NinetailedContentfulModule = ContentfulModule & {
  id: string;
  hidden: boolean;
};

export const getPageSectionTypenames = async (entryId: string, draftPreviewEnabled = false) => {
  const response = await fetchGraphQL<PageSectionTypenamesResponse>(
    `query GetPageSectionTypenames {
      pageSection(id: "${entryId}", preview: ${draftPreviewEnabled}) {
        modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
          items {
            __typename
          }
        }
      }
    }`,
    draftPreviewEnabled,
  );

  const typenames = response?.data?.pageSection?.modulesCollection?.items.map(item => item?.__typename) ?? [];

  return typenames;
};

const getPageSection = async (entryId: string, draftPreviewEnabled: boolean) => {
  const typenames = await getPageSectionModuleTypenames(entryId, draftPreviewEnabled);
  const pageModuleFragments = getModuleFragments(typenames);

  return `query {
    pageSection(id: "${entryId}", preview: ${draftPreviewEnabled}) {
      sys {
        id
      }
      internalName
      modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
        items {
          __typename
          ${pageModuleFragments}
        }
      }
      ntExperiencesCollection(limit: ${MAX_EXPERIENCES_PER_PAGE_SECTION}) {
        items {
          ... on NtExperience {
            sys {
              id
            }
            ntName
            snowflakeName
            ntDescription
            ntAudience {
              sys {
                id
              }
              ntName
            }
            ntVariantsCollection(limit: ${MAX_VARIANTS_PER_EXPERIENCE}) {
              items {
                sys {
                  id
                }
              }
            }
          }
        }
      }
    }
  }`;
};

async function getPageSectionModuleTypenames(entryId: string, draftPreviewEnabled: boolean) {
  const response = await fetchGraphQL(
    `query GetPageModuleTypenames {
      pageSection(id: "${entryId}", preview: ${draftPreviewEnabled}) {
        modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
          items {
            __typename
          }
        }
      }
    }`,
    draftPreviewEnabled,
  );

  // The response structure is different here - it's a single pageSection, not a collection
  const page = response?.data?.pageSection as { modulesCollection?: { items: Array<{ __typename?: string }> } };

  // Access modulesCollection directly since it's at the top level
  const modules = page?.modulesCollection?.items || [];
  const typenames = getUniqueTypenames(modules);

  return typenames;
}

const getVariantModuleTypenames = async (variantIds: string[], draftPreviewEnabled: boolean) => {
  if (variantIds.length === 0) {
    return new Map<string, string[]>();
  }

  const variantQueries = variantIds
    .map(
      (variantId, index) => `
      variant${index}: pageSection(id: "${variantId}", preview: ${draftPreviewEnabled}) {
        modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
          items {
            __typename
          }
        }
      }
    `,
    )
    .join('\n');

  const response = await fetchGraphQL<VariantModuleTypenamesResponse>(
    `query GetVariantModuleTypenames {
      ${variantQueries}
    }`,
    draftPreviewEnabled,
  );

  const result = new Map<string, string[]>();
  variantIds.forEach((variantId, index) => {
    const key = `variant${index}`;
    const items = response?.data?.[key]?.modulesCollection?.items;
    result.set(variantId, getUniqueTypenames(items ?? []));
  });

  return result;
};

const buildPageSectionBaseQuery = (entryId: string, draftPreviewEnabled: boolean, moduleFragments: string) => `
  pageSection(id: "${entryId}", preview: ${draftPreviewEnabled}) {
    sys {
      id
    }
    modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
      items {
        __typename
        ${moduleFragments}
      }
    }
    ntExperiencesCollection(limit: ${MAX_EXPERIENCES_PER_PAGE_SECTION}) {
      items {
        ... on NtExperience {
          sys {
            id
          }
          ntName
          snowflakeName
          ntDescription
          ntConfig
          ntAudience {
            sys {
              id
            }
            ntName
          }
          ntVariantsCollection(limit: ${MAX_VARIANTS_PER_EXPERIENCE}) {
            items {
              sys {
                id
              }
            }
          }
        }
      }
    }
  }
`;

type VariantQueryEntry = {
  key: string;
  query: string;
};

const buildVariantQueryEntries = (
  variantIds: string[],
  draftPreviewEnabled: boolean,
  variantTypenamesMap: Map<string, string[]>,
): VariantQueryEntry[] =>
  variantIds.map((variantId, index) => {
    const variantTypenames = variantTypenamesMap.get(variantId) ?? [];
    const variantModuleFragments = getModuleFragments(variantTypenames);
    const key = `variant${index}`;

    return {
      key,
      query: `
          ${key}: pageSection(id: "${variantId}", preview: ${draftPreviewEnabled}) {
            sys {
              id
            }
            internalName
            modulesCollection(limit: ${MAX_MODULES_PER_PAGE_SECTION}) {
              items {
                __typename
                ${variantModuleFragments}
              }
            }
          }
        `,
    };
  });

const buildPageSectionBatchedQuery = (
  baseQuery: string,
  variantQueries: VariantQueryEntry[],
  includeBaseQuery: boolean,
) => `
  query {
    ${includeBaseQuery ? baseQuery : ''}
    ${variantQueries.map(entry => entry.query).join('\n')}
  }
`;

const buildPageSectionVariantQueries = async (
  entryId: string,
  variantIds: string[],
  draftPreviewEnabled: boolean,
  variantTypenamesMap: Map<string, string[]>,
) => {
  if (variantIds.length === 0) {
    return [appendFragmentsToQuery(await getPageSection(entryId, draftPreviewEnabled))];
  }

  const variantEntries = buildVariantQueryEntries(variantIds, draftPreviewEnabled, variantTypenamesMap);
  // We don't include baseQuery in batched queries since we already have base data from the initial query
  const queries = variantEntries.map(entry => {
    const query = appendFragmentsToQuery(buildPageSectionBatchedQuery('', [entry], false));
    const querySizeRatio = query.length / CONTENTFUL_QUERY_SIZE_LIMIT;

    if (querySizeRatio > 0.5) {
      const percentageOfLimit = Math.round(querySizeRatio * 100);
      console.warn(
        `[Contentful Query Size] PageSection variants query at ${percentageOfLimit}% of limit for entryId ${entryId}.`,
      );
    }

    if (query.length > CONTENTFUL_QUERY_SIZE_LIMIT) {
      console.warn(`[Contentful Query Size] PageSection variants query exceeds limit for entryId ${entryId}.`);
    }

    return query;
  });

  return queries;
};

/**
 * Handles Ninetailed data fetching for PageSection modules using GraphQL
 */
async function getPageSectionNinetailedData(entryId: string, draftPreviewEnabled: boolean) {
  // First, get the basic page section to identify all variant IDs
  const initialResponse = await fetchGraphQL(
    appendFragmentsToQuery(await getPageSection(entryId, draftPreviewEnabled)),
    draftPreviewEnabled,
  );
  const rawEntry = initialResponse?.data?.pageSection as NinetailedResponse;

  if (!rawEntry) {
    console.error('No Ninetailed data found for entryId', entryId);
    return {
      baselineEntry: null,
      mappedExperiences: [] as ExperienceConfiguration<ContentfulModule & { id: string }>[],
    };
  }

  // Collect all variant IDs from all experiences
  const allVariantIds = rawEntry.ntExperiencesCollection.items
    .flatMap(exp => exp.ntVariantsCollection.items.map(variant => variant.sys.id))
    .filter(Boolean);

  const variantTypenamesMap = await getVariantModuleTypenames(allVariantIds, draftPreviewEnabled);
  const batchedQueries = await buildPageSectionVariantQueries(
    entryId,
    allVariantIds,
    draftPreviewEnabled,
    variantTypenamesMap,
  );
  const batchedResponses = await Promise.all(
    batchedQueries.map(query => fetchGraphQL<{ data?: Record<string, unknown> }>(query, draftPreviewEnabled)),
  );
  const fullData = batchedResponses.reduce<Record<string, unknown>>((acc, response) => {
    const data = response?.data;
    if (data) {
      Object.assign(acc, data);
    }
    return acc;
  }, {});

  if (Object.keys(fullData).length === 0) {
    console.error('Failed to fetch complete Ninetailed data for entryId', entryId);
    return {
      baselineEntry: null,
      mappedExperiences: [] as ExperienceConfiguration<ContentfulModule & { id: string }>[],
    };
  }

  // Transform the entry to match PageSection props
  const baselineEntry = {
    sys: rawEntry.sys,
    __typename: rawEntry.__typename,
    modulesCollection: {
      items: rawEntry.modulesCollection.items.map(item => ({
        ...item,
        __typename: item?.__typename,
        sys: item?.sys,
        id: item?.sys?.id,
        modulesCollection: { items: [] },
      })) as Array<ModuleTypes[ModuleTypeName]>,
    },
  };

  // Create a map of variant data for quick lookup
  const variantDataMap = new Map<
    string,
    {
      id: string;
      internalName?: string;
      modules: Array<ContentfulModule & { id: string }>;
    }
  >();

  // Extract variant data from the response
  Object.entries(fullData).forEach(([key, value]) => {
    if (key.startsWith('variant') && value) {
      const variantSection = value as {
        sys: { id: string };
        internalName?: string;
        modulesCollection: { items: Array<ContentfulModule> };
      };

      const modules = variantSection.modulesCollection.items.map(item => ({
        ...item,
        __typename: item.__typename,
        sys: item?.sys,
        id: item?.sys?.id,
      }));

      variantDataMap.set(variantSection.sys.id, {
        id: variantSection.sys.id,
        internalName: variantSection.internalName,
        modules,
      });
    }
  });

  const mappedExperiences = rawEntry.ntExperiencesCollection.items
    .filter(exp => exp.sys?.id)
    .map(exp => {
      // Get variant data from the map - preserve positions, mark empty variants as hidden
      const variantData = exp.ntVariantsCollection.items.map(variant =>
        variant?.sys?.id ? variantDataMap.get(variant.sys.id) : null,
      );

      // Create experience configuration
      const experience = {
        id: exp.sys.id,
        name: exp.ntName ?? exp.sys.id,
        snowflakeName: exp.snowflakeName,
        type: 'nt_experiment',
        description: exp.ntDescription ?? '',
        config: {
          components: [
            {
              baseline: { id: entryId },
              variants: variantData.map((v, i) =>
                v ? { id: v.id, hidden: false } : { id: `empty-${i}`, hidden: true },
              ),
            },
          ],
        },
        variants: variantData.map((v, i) => (v ? { id: v.id, hidden: false } : { id: `empty-${i}`, hidden: true })),
        ...(exp.ntAudience && {
          audience: {
            id: exp.ntAudience.sys.id,
            name: exp.ntAudience.ntName ?? exp.ntAudience.sys.id,
          },
        }),
      };

      if (!GraphQLExperienceMapper.isExperienceEntry(experience)) {
        return null;
      }

      const mapped = GraphQLExperienceMapper.mapExperience(
        experience,
      ) as ExtendedExperienceConfiguration<NinetailedContentfulModule>;

      // Preserve snowflakeName since the mapper doesn't handle custom properties
      if (experience.snowflakeName) {
        mapped.snowflakeName = experience.snowflakeName;
      }

      // Transform components for PageSection format
      if (mapped.components?.length) {
        mapped.components = mapped.components.map(component => {
          switch (component.type) {
            case 'InlineVariable':
              return {
                ...component,
                baseline: { value: component.baseline?.value || entryId },
                variants:
                  component.variants?.map(variant => {
                    const variantDataItem = variantData.find(v => v?.id === variant.value);
                    // Empty variant (null in variantData) should be hidden
                    if (!variantDataItem) {
                      return { value: variant.value, hidden: true, modulesCollection: { items: [] } };
                    }
                    return {
                      value: variant.value,
                      internalName: variantDataItem.internalName,
                      modulesCollection: { items: variantDataItem.modules || [] },
                    };
                  }) || [],
              };
            case 'EntryReplacement':
              return {
                ...component,
                baseline: { id: component.baseline?.id || entryId },
                variants:
                  component.variants?.map(variant => {
                    const variantDataItem = variantData.find(v => v?.id === variant.id);
                    // Empty variant (null in variantData) should be hidden
                    if (!variantDataItem) {
                      return { id: variant.id, hidden: true, modulesCollection: { items: [] } };
                    }
                    return {
                      id: variant.id,
                      hidden: false,
                      internalName: variantDataItem.internalName,
                      modulesCollection: { items: variantDataItem.modules || [] },
                    };
                  }) || [],
              };
            default:
              return component;
          }
        });
      }

      return mapped;
    });

  return {
    baselineEntry,
    mappedExperiences: filterValidExperiences(mappedExperiences),
  };
}

/**
 * Get full section module data for variant entries using the generic processor
 */
async function getSectionModuleExperiences(
  mappedExperiences: ExtendedExperienceConfiguration<NinetailedContentfulModule>[],
  sectionType: keyof typeof SECTION_MODULE_CONFIG,
) {
  const config = SECTION_MODULE_CONFIG[sectionType];
  if (!config) {
    console.warn(`No configuration found for section type: ${sectionType}`);
    return mappedExperiences;
  }

  return processSectionModuleExperiences(mappedExperiences, sectionType, config.fetchFunction, config.dataProperty);
}

/**
 * Handles Ninetailed data fetching for individual modules using GraphQL
 */
async function getModuleNinetailedData(entryId: string, moduleName: string, draftPreviewEnabled: boolean) {
  // Get the module fragment for this specific module type
  const moduleFragment = getModuleFragments([moduleName]);

  // Convert the module name to camel case for the GraphQL query
  const moduleNameCamel = pascalToCamel(moduleName);

  // Create GraphQL query for the individual module
  const query = `
    query GetModuleWithExperiences {
      ${moduleNameCamel}(id: "${entryId}", preview: ${draftPreviewEnabled}) {
        sys {
          id
        }
        __typename
        internalName
        ${moduleFragment}
        ntExperiencesCollection(limit: ${MAX_EXPERIENCES_PER_PAGE_SECTION}) {
          items {
            ... on NtExperience {
              sys {
                id
              }
              ntName
              ntType
              snowflakeName
              ntDescription
              ntConfig
              ntAudience {
                sys {
                  id
                }
                ntName
              }
              ntVariantsCollection(limit: ${MAX_VARIANTS_PER_EXPERIENCE}) {
                items {
                  sys {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetchGraphQL<{ data?: { [key: string]: IndividualModuleResponse } }>(
    appendFragmentsToQuery(query),
    draftPreviewEnabled,
  );

  const rawEntry = response?.data?.[moduleNameCamel] as IndividualModuleResponse;

  if (!rawEntry) {
    console.error('Module:', moduleName, 'No Ninetailed data found for entryId', entryId);
    return {
      baselineEntry: null,
      mappedExperiences: [] as ExperienceConfiguration<ContentfulModule & { id: string }>[],
    };
  }

  // Transform the entry to match module props
  const baselineEntry = {
    sys: rawEntry.sys,
    __typename: rawEntry.__typename,
    ...Object.fromEntries(
      Object.entries(rawEntry).filter(
        ([key]) => key !== 'sys' && key !== '__typename' && key !== 'ntExperiencesCollection',
      ),
    ),
  };

  // Create a map of variant data for quick lookup
  const variantDataMap = new Map<
    string,
    {
      id: string;
      internalName?: string;
      data: Record<string, unknown>;
    }
  >();

  // Collect all variant IDs from all experiences
  const allVariantIds = rawEntry.ntExperiencesCollection.items
    .flatMap(exp => exp.ntVariantsCollection.items.map(variant => variant.sys.id))
    .filter(Boolean);

  // If there are variants, fetch them in a single query
  if (allVariantIds.length > 0) {
    const variantQueries = allVariantIds.map(
      (variantId, index) => `
      variant${index}: ${moduleNameCamel}(id: "${variantId}", preview: ${draftPreviewEnabled}) {
        sys {
          id
        }
        __typename
        internalName
        ${moduleFragment}
      }
    `,
    );

    const variantQuery = `
      query GetModuleVariants {
        ${variantQueries.join('\n')}
      }
    `;

    const variantResponse = await fetchGraphQL<{ data?: Record<string, IndividualModuleResponse> }>(
      appendFragmentsToQuery(variantQuery),
      draftPreviewEnabled,
    );

    const variantData = variantResponse?.data;
    if (variantData) {
      Object.entries(variantData).forEach(([key, value]) => {
        if (key.startsWith('variant') && value) {
          const variantEntry = value as IndividualModuleResponse;
          variantDataMap.set(variantEntry.sys.id, {
            id: variantEntry.sys.id,
            internalName: variantEntry.internalName,
            data: Object.fromEntries(
              Object.entries(variantEntry).filter(
                ([k]) => k !== 'sys' && k !== '__typename' && k !== 'ntExperiencesCollection' && k !== 'internalName',
              ),
            ),
          });
        }
      });
    }
  }

  const mappedExperiences = rawEntry.ntExperiencesCollection.items
    .filter(exp => exp.sys?.id)
    .map(exp => {
      // Parse ntConfig to find the component for this specific module
      const ntConfig = (
        exp as unknown as {
          ntConfig?: {
            components?: Array<{ baseline?: { id?: string }; variants?: Array<{ id?: string; hidden?: boolean }> }>;
          };
        }
      ).ntConfig;

      // Determine variant data based on ntConfig presence:
      // - If ntConfig is missing (older entries), fall back to ALL variants from ntVariantsCollection
      // - If ntConfig exists but no matching component, return empty (module not part of this experience)
      // - If ntConfig exists with matching component, use only that component's variants
      type VariantDataItem = { id: string; internalName?: string; data: Record<string, unknown> } | null | undefined;
      let variantData: VariantDataItem[];

      if (!ntConfig) {
        // Fallback for entries without ntConfig: use all variants from ntVariantsCollection
        variantData = exp.ntVariantsCollection.items.map(variant =>
          variant?.sys?.id ? variantDataMap.get(variant.sys.id) : null,
        );
      } else {
        const moduleComponent = ntConfig.components?.find(comp => comp.baseline?.id === entryId);
        // Get variant IDs for this specific component from ntConfig
        // Note: We preserve positions by NOT filtering - variants without IDs become undefined
        const componentVariantIds = moduleComponent?.variants?.map(v => v.id) ?? [];
        // Get variant data ONLY for this component's variants, in order
        variantData = componentVariantIds.map(variantId => (variantId ? variantDataMap.get(variantId) : null));
      }

      // Create experience configuration using the resolved variants for this component
      const experience = {
        id: exp.sys.id,
        name: exp.ntName ?? exp.sys.id,
        snowflakeName: exp.snowflakeName,
        type: exp.ntType || 'nt_experiment',
        description: exp.ntDescription ?? '',
        config: {
          components: [
            {
              baseline: { id: entryId },
              variants: variantData.map((v, i) =>
                v ? { id: v.id, hidden: false } : { id: `empty-${i}`, hidden: true },
              ),
            },
          ],
        },
        variants: variantData.map((v, i) => (v ? { id: v.id, hidden: false } : { id: `empty-${i}`, hidden: true })),
        ...(exp.ntAudience && {
          audience: {
            id: exp.ntAudience.sys.id,
            name: exp.ntAudience.ntName ?? exp.ntAudience.sys.id,
          },
        }),
      };

      if (!GraphQLExperienceMapper.isExperienceEntry(experience)) {
        return null;
      }

      const mapped = GraphQLExperienceMapper.mapExperience(
        experience,
      ) as ExtendedExperienceConfiguration<NinetailedContentfulModule>;

      // Preserve snowflakeName since the mapper doesn't handle custom properties
      if (experience.snowflakeName) {
        mapped.snowflakeName = experience.snowflakeName;
      }

      // Transform components for individual module format
      if (mapped.components?.length) {
        mapped.components = mapped.components.map(component => {
          switch (component.type) {
            case 'InlineVariable':
              return {
                ...component,
                baseline: { value: component.baseline?.value || entryId },
                variants:
                  component.variants?.map(variant => {
                    const variantDataItem = variantData.find(v => v?.id === variant.value);
                    // Empty variant (null in variantData) should be hidden
                    if (!variantDataItem) {
                      return { value: variant.value, hidden: true };
                    }
                    return {
                      value: variant.value,
                      internalName: variantDataItem.internalName,
                      ...variantDataItem.data,
                    };
                  }) || [],
              };
            case 'EntryReplacement':
              return {
                ...component,
                baseline: { id: component.baseline?.id || entryId },
                variants:
                  component.variants?.map(variant => {
                    const variantDataItem = variantData.find(v => v?.id === variant.id);
                    // Empty variant (null in variantData) should be hidden
                    if (!variantDataItem) {
                      return { id: variant.id, hidden: true };
                    }
                    return {
                      id: variant.id,
                      hidden: false,
                      internalName: variantDataItem.internalName,
                      ...variantDataItem.data,
                    };
                  }) || [],
              };
            default:
              return component;
          }
        });
      }

      return mapped;
    });

  // For section modules, get full data using the generic processor
  if (moduleName in SECTION_MODULE_CONFIG) {
    const mergedExperiences = await getSectionModuleExperiences(
      mappedExperiences as ExtendedExperienceConfiguration<NinetailedContentfulModule>[],
      moduleName as keyof typeof SECTION_MODULE_CONFIG,
    );

    return {
      baselineEntry,
      mappedExperiences: filterValidExperiences(
        mergedExperiences as ExtendedExperienceConfiguration<NinetailedContentfulModule>[],
      ),
    };
  }

  return {
    baselineEntry,
    mappedExperiences: filterValidExperiences(mappedExperiences),
  };
}

/**
 * Fetches and processes Ninetailed experience data for a given entry
 * @param entryId - The Contentful entry ID to fetch experiences for
 * @param moduleName - The name of the module type
 * @returns Object containing the baseline entry and mapped experiences
 */
export async function getNinetailedData(entryId: string, moduleName: string) {
  const isPageSection = moduleName === 'PageSection';
  const draftPreviewEnabled = await isDraftPreviewEnabled();

  if (isPageSection) {
    return getPageSectionNinetailedData(entryId, draftPreviewEnabled);
  } else {
    return getModuleNinetailedData(entryId, moduleName, draftPreviewEnabled);
  }
}
